'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { useToast } from '@/components/ToastProvider';
import { buildExploreActionRecipe } from '@/lib/social/gate-d/recreate-client';
import { consumeFeaturedRewardReturnLocation } from '@/lib/social/featured-reward-notice';
import PublicPostPreview from './PublicPostPreview';

const RETURN_PATHS = Object.freeze({
  home: '/',
  explore: '/explore',
  profile: '/profile',
  notifications: '/profile',
});

export default function PublicPostRoutePreview({
  post,
  returnSource = null,
  canDelete = false,
  isFeatured = false,
}) {
  const t = useTranslations('GateD');
  const router = useRouter();
  const { showToast } = useToast();
  const close = useCallback((index) => {
    if (index !== null) return;
    const rememberedPath = consumeFeaturedRewardReturnLocation(post?.id);
    if (rememberedPath) {
      window.location.assign(rememberedPath);
      return;
    }
    if (returnSource === 'notifications') {
      try {
        const referrer = document.referrer ? new URL(document.referrer) : null;
        if (
          referrer?.origin === window.location.origin
          && referrer.href !== window.location.href
          && window.history.length > 1
        ) {
          window.history.back();
          return;
        }
      } catch {
        // Invalid or unavailable referrer falls through to the safe Profile.
      }
    }
    router.replace(RETURN_PATHS[returnSource] || '/explore');
  }, [post?.id, returnSource, router]);

  const deletePost = useCallback(async (item) => {
    const response = await fetch(`/api/profile/posts/${item.id}`, { method: 'DELETE' });
    await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(t('common.postDeleteFailed'));
    }
    showToast({
      title: t('common.postDeleted'),
      description: t('common.originalAssetAvailable'),
      type: 'success',
    });
    router.replace('/profile');
  }, [router, showToast, t]);

  const openAction = useCallback(async (item, action, overrides = {}) => {
    try {
      const response = await fetch(`/api/posts/${item.id}/recreate-intent`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(t('common.signInToUseCreation'));
      }
      const recipe = buildExploreActionRecipe(item, action, overrides);
      sessionStorage.setItem('botgo.recreate.intent.v1', body.token);
      sessionStorage.setItem('botgo.recreate.recipe.v1', JSON.stringify(recipe));
      router.push('/create?recreate=1');
    } catch (error) {
      showToast({
        title: t('common.actionUnavailableTitle'),
        description: error?.message || t('common.pleaseTryAgain'),
        type: 'error',
      });
    }
    return false;
  }, [router, showToast, t]);

  return (
    <PublicPostPreview
      items={[{ ...post, isFeatured }]}
      activeIndex={0}
      onIndexChange={close}
      onRouteAction={openAction}
      canDeleteItems={canDelete}
      showFeaturedBadge={canDelete}
      onDeleteItem={canDelete ? deletePost : null}
    />
  );
}
